import type { EditorHost } from '@blocksuite/block-std';
import type {
  EdgelessElementToolbarWidget,
  ShapeElementModel,
  SurfaceBlockModel,
} from '@blocksuite/blocks';
import {
  EDGELESS_ELEMENT_TOOLBAR_WIDGET,
  EDGELESS_TEXT_BLOCK_MIN_HEIGHT,
  EDGELESS_TEXT_BLOCK_MIN_WIDTH,
  EdgelessTextBlockModel,
  fitContent,
  ImageBlockModel,
  LayoutType,
  MindmapElementModel,
  MindmapStyle,
  NoteDisplayMode,
} from '@blocksuite/blocks';
import { Bound, getCommonBound } from '@blocksuite/global/utils';
import type { PiqueMindmapElementModel } from '@pique/core/components/blocksuite/block-suite-editor/specs/custom/pique/mindmap-element';
import { posthog } from '@pique/core/posthog';

interface NodeType {
  text: string;
  xywh?: string;
  children?: NodeType[];
}
import { type TemplateResult } from 'lit';

import { insertFromMarkdown } from '../_common/markdown-utils';
import { type MermaidTemplate } from '../mermaid/template';
import type { AIMermaidRenderer } from '../messages/mermaid-renderer';
import { renderBlocksuiteElements } from '../messages/mermaid-renderer';
import type { AIQuizRenderer } from '../messages/quiz-processor';
import {
  createMindmapTagElements,
  determineInsertionBounds,
  getAIPanel,
  getEdgelessCopilotWidget,
  getService,
  isMindMapRoot,
  type MindmapMetadata,
} from '../utils/edgeless';
import { preprocessHtml } from '../utils/html';
import { fetchImageToFile } from '../utils/image';
import {
  getCopilotSelectedElems,
  getEdgelessRootFromEditor,
  getEdgelessService,
} from '../utils/selection-utils';
import { generatingStages } from './consts';
import type { CtxRecord } from './types';

// Reads directly from localStorage since Jotai store is not accessible from blocksuite context.
// This mirrors the value from detailLevelPreferenceAtom (atomWithStorage uses same key).
function getDetailLevelFromPreference(): number {
  try {
    const stored = localStorage.getItem('pique-detail-level-preference');
    const preference = stored ? JSON.parse(stored) : 'balanced';
    switch (preference) {
      case 'concise':
        return 1;
      case 'comprehensive':
        return 999;
      case 'balanced':
      default:
        return 2;
    }
  } catch {
    return 2;
  }
}

export function getElementToolbar(
  host: EditorHost
): EdgelessElementToolbarWidget {
  const rootBlockId = host.doc.root?.id as string;
  const elementToolbar = host.view.getWidget(
    EDGELESS_ELEMENT_TOOLBAR_WIDGET,
    rootBlockId
  ) as EdgelessElementToolbarWidget;

  return elementToolbar;
}

export const extraConditions: Record<
  string,
  (data: Record<string, unknown>) => boolean
> = {
  createSlides: data => !!data['contents'],
};

async function insertBelow(
  host: EditorHost,
  markdown: string,
  parentId: string,
  index = 0
) {
  await insertFromMarkdown(host, markdown, parentId, index);
  getService(host).selection.set({ elements: [parentId], editing: false });
}

async function createBlockAndInsert(
  host: EditorHost,
  markdown: string,
  type: 'edgelessText' | 'note'
) {
  const doc = host.doc;
  if (!doc.root) return;

  const surfaceBlock = doc.getBlocksByFlavour('affine:surface')[0];
  if (!surfaceBlock) return;

  let blockId = '';
  doc.transact(() => {
    if (type === 'edgelessText') {
      blockId = doc.addBlock(
        'affine:edgeless-text',
        {
          xywh: determineInsertionBounds(
            host,
            EDGELESS_TEXT_BLOCK_MIN_WIDTH,
            EDGELESS_TEXT_BLOCK_MIN_HEIGHT
          ).serialize(),
        },
        surfaceBlock.id
      );
    } else {
      blockId = doc.addBlock(
        'affine:note',
        {
          xywh: determineInsertionBounds(host, 800, 95).serialize(),
          displayMode: NoteDisplayMode.EdgelessOnly,
        },
        doc.root!.id
      );
    }
  });

  await insertBelow(host, markdown, blockId);
}

export const defaultHandler = (host: EditorHost) => {
  const panel = getAIPanel(host);
  if (!panel?.answer) return;
  const selectedElements = getCopilotSelectedElems(host);

  const firstElement = selectedElements[0] as
    | BlockSuite.EdgelessModel
    | undefined;
  if (
    selectedElements.length === 1 &&
    firstElement instanceof EdgelessTextBlockModel
  ) {
    const edgelessTextBlockId = firstElement.id;
    const index = firstElement.children.length;
    insertBelow(host, panel.answer, edgelessTextBlockId, index);
  } else if (
    selectedElements.length > 1 &&
    selectedElements.every(el => el instanceof EdgelessTextBlockModel)
  ) {
    createBlockAndInsert(host, panel.answer, 'edgelessText');
  } else {
    createBlockAndInsert(host, panel.answer, 'note');
  }
};

const imageHandler = async (host: EditorHost) => {
  const aiPanel = getAIPanel(host);
  if (!aiPanel) return;
  const data = aiPanel.answer;
  if (!data) return;

  const bounds = determineInsertionBounds(host);
  const selectedElements = getCopilotSelectedElems(host);
  const selectedImageBlockModel = selectedElements.find(
    model => model instanceof ImageBlockModel
  );
  const selectedBound = selectedImageBlockModel
    ? Bound.deserialize(selectedImageBlockModel.xywh)
    : null;

  const edgelessCopilot = getEdgelessCopilotWidget(host);
  edgelessCopilot.hideCopilotPanel();
  aiPanel.hide();

  const imageProxy = host.std.clipboard.configs.get('imageProxy');
  const img = await fetchImageToFile(data, 'image', imageProxy);
  if (!img) return;

  const edgelessRoot = getEdgelessRootFromEditor(host);
  const { minX, minY } = bounds;

  const blockIds = await edgelessRoot.addImages([img], [minX, minY], true);
  const imageBlockId = blockIds[0];
  if (!imageBlockId || !selectedBound) return;

  const imageBlock = host.doc.getBlock(imageBlockId);
  if (!imageBlock) return;

  const imageModel = imageBlock.model as ImageBlockModel;
  const imageBound = Bound.deserialize(imageModel.xywh);
  host.doc.updateBlock(imageModel, {
    xywh: new Bound(imageBound.x, imageBound.y, selectedBound.w, selectedBound.h).serialize(),
  });
};

export const responses: {
  [key in keyof Partial<BlockSuitePresets.AIActions>]: (
    host: EditorHost,
    ctx: CtxRecord
  ) => void | Promise<void>;
} = {
  expandMindmap: async (host, ctx) => {
    const surface = host.doc.getBlocksByFlavour('affine:surface')[0]
      ?.model as SurfaceBlockModel | undefined;
    if (!surface) return;
    const data = ctx.get() as { node: NodeType };

    queueMicrotask(() => getAIPanel(host)?.hide());

    // Find the mindmap element
    const edgelessService = getEdgelessService(host);
    const mindmapElements = (
      edgelessService.edgelessElements as Array<{ group?: unknown }>
    ).filter(
      (element: { group?: unknown }) =>
        element.group instanceof MindmapElementModel
    );
    const firstMindmapElement = mindmapElements[0];
    if (!firstMindmapElement) return;
    const mindmap = firstMindmapElement.group as MindmapElementModel;

    if (!mindmap || !data?.node?.text) return;

    // Find the parent node by text content
    let parentNodeId = '';
    mindmap.traverse(node => {
      if (node.element && 'text' in node.element && node.element.text) {
        const element = node.element as ShapeElementModel;
        const nodeText = element.text
          ?.toString()
          .replace(/\n/g, '')
          .toLowerCase()
          .trim();
        const dataNodeText = data.node.text
          .replace(/\n/g, '')
          .toLowerCase()
          .trim();
        if (nodeText === dataNodeText) {
          parentNodeId = node.id;
        }
      }
    });

    if (!parentNodeId || !data.node.children?.length) return;

    // Highlight the parent node before expanding it
    if (
      'highlightNode' in mindmap &&
      typeof mindmap.highlightNode === 'function'
    ) {
      mindmap.highlightNode(parentNodeId, { duration: 800 });
    }

    // Wait for highlight animation to complete (non-blocking)
    await new Promise(resolve => setTimeout(resolve, 850));

    // Add children after the highlight animation
    data.node.children?.forEach((childTree: NodeType) => {
      mindmap.addTree(parentNodeId, childTree);
    });

    const subtree = mindmap.getNode(parentNodeId);

    if (!subtree) return;

    surface.doc.transact(() => {
      const updateNodeSize = (node: typeof subtree) => {
        fitContent(node.element as ShapeElementModel);
        node.children.forEach(updateNodeSize);
      };
      updateNodeSize(subtree);
    });

    // Yield to ensure tree structure is updated before applying metadata
    await new Promise<void>(resolve => queueMicrotask(() => resolve()));

    // Apply metadata after tree is fully updated
    const metadataFromStep2 = (data as Record<string, unknown>)['metadata'] as
      | MindmapMetadata[]
      | undefined;
    if (metadataFromStep2 && Array.isArray(metadataFromStep2)) {
      try {
        createMindmapTagElements(
          host,
          mindmap as PiqueMindmapElementModel,
          metadataFromStep2
        );
      } catch (err) {
        console.warn('[expandMindmap] failed to create tag elements', err);
      }
    }

    // Zoom to the updated mindmap and select the parent node for continuity
    await new Promise(resolve => setTimeout(resolve, 50));

    edgelessService.viewport.setViewportByBound(
      mindmap.elementBound,
      [10, 10, 10, 10],
      true
    );

    edgelessService.selection.set({
      elements: [parentNodeId],
      editing: false,
    });
  },

  brainstormMindmap: async (host, ctx) => {
    const aiPanel = getAIPanel(host);
    if (!aiPanel) return;
    const edgelessService = getEdgelessService(host);
    const edgelessCopilot = getEdgelessCopilotWidget(host);
    const selectionRect = edgelessCopilot.selectionModelRect;
    const surface = host.doc.getBlocksByFlavour('affine:surface')[0]
      ?.model as SurfaceBlockModel | undefined;
    if (!surface) return;

    posthog.capture('mindmap_opened', { source: 'brainstorm' });

    const elements = ctx.get()[
      'selectedElements'
    ] as BlockSuite.EdgelessModel[];
    const data = ctx.get() as Record<string, unknown>;
    let isRegeneration = false;

    edgelessService?.hideAllElements();

    // Handle regeneration case
    const firstElement = elements[0];
    if (firstElement && isMindMapRoot(firstElement)) {
      const mindmap = firstElement.group as MindmapElementModel;
      if (mindmap.tree?.element) {
        const xywh = mindmap.tree.element.xywh;
        surface.removeElement(mindmap.id);

        if (
          data['node'] &&
          typeof data['node'] === 'object' &&
          data['node'] !== null
        ) {
          (data['node'] as Record<string, unknown>)['xywh'] = xywh;
          isRegeneration = true;
        }
      } else {
        surface.removeElement(mindmap.id);
      }
    }

    // Clean up UI
    edgelessCopilot.hideCopilotPanel();
    aiPanel.hide();

    // Remove all existing mindmap elements
    const existingMindmaps = (
      edgelessService.edgelessElements as Array<{ group?: unknown; id: string }>
    ).filter(
      (element: { group?: unknown }) =>
        element.group instanceof MindmapElementModel
    );
    existingMindmaps.forEach((mindmap: { id: string }) =>
      edgelessService.removeElement(mindmap.id)
    );

    const processNodeText = (node: NodeType): NodeType => {
      if (!node || typeof node !== 'object') return node;

      const processedNode = { ...node };

      if (typeof processedNode.text === 'string') {
        const text = processedNode.text;
        const words = text.split(' ');

        if (!text.includes('\n') && words.length > 2) {
          let formatted = '';
          let counter = 0;
          const maxPerLine = 3;

          words.forEach((w: string, i: number) => {
            formatted += w;
            counter++;

            if (i < words.length - 1) {
              if (counter >= maxPerLine) {
                formatted += '\n';
                counter = 0;
              } else {
                formatted += ' ';
              }
            }
          });

          processedNode.text = formatted;
        }
      }

      if (Array.isArray(processedNode.children)) {
        processedNode.children = processedNode.children.map(processNodeText);
      }

      return processedNode;
    };

    const processedNodeData = processNodeText(data['node'] as NodeType);

    // Yield before expensive DOM operation
    await new Promise<void>(resolve => queueMicrotask(() => resolve()));

    // Create new mindmap
    const mindmapId = surface.addElement({
      type: 'mindmap',
      children: processedNodeData,
      style: MindmapStyle.ONE,
      layoutType: LayoutType.RIGHT,
    });

    const mindmap = surface.getElementById(
      mindmapId
    ) as PiqueMindmapElementModel;

    // Yield before content fitting operations (ensures tree is initialized)
    await new Promise<void>(resolve => queueMicrotask(() => resolve()));

    const metadataIncoming = data['metadata'] as MindmapMetadata[] | undefined;
    if (metadataIncoming && metadataIncoming.length > 0) {
      createMindmapTagElements(host, mindmap, metadataIncoming);
    }

    // Fit content and track creation
    host.doc.transact(() => {
      mindmap.childElements.forEach(shape => {
        fitContent(shape as ShapeElementModel);
      });
    });

    // Yield before positioning operations
    await new Promise<void>(resolve => queueMicrotask(() => resolve()));

    // Always apply detail level from user preference (handles zoom)
    mindmap.setDetailLevel(getDetailLevelFromPreference());

    // Position mindmap if we have a selection rect
    if (!isRegeneration && selectionRect) {
      mindmap.moveTo([
        selectionRect.x,
        selectionRect.y,
        selectionRect.width,
        selectionRect.height,
      ]);
    }

    setTimeout(() => {
      if (mindmap.tree) {
        edgelessService.selection.set({
          elements: [mindmap.tree.element.id],
          editing: false,
        });
      }
    }, 100);
  },

  createMermaid: async (host: EditorHost, ctx: CtxRecord) => {
    const aiPanel = getAIPanel(host);
    if (!aiPanel) return;
    const edgelessService = getEdgelessService(host);
    const surface = host.doc.getBlocksByFlavour('affine:surface')[0]
      ?.model as SurfaceBlockModel | undefined;
    if (!surface) return;

    const ctxData = ctx.get();
    if (ctxData?.['error']) throw new Error(String(ctxData['error']));

    const renderer = aiPanel.shadowRoot?.querySelector(
      'ai-mermaid-renderer'
    ) as AIMermaidRenderer | undefined;
    if (!renderer) throw new Error('No mermaid renderer found');

    await renderer.templateReady;

    const data = ctx.get();
    if (!data) throw new Error('No data available');

    const template = data['data'] as MermaidTemplate;
    edgelessService.hideAllElements();

    const elementIds = await renderBlocksuiteElements(
      host,
      edgelessService,
      surface,
      template
    );

    if (elementIds.length > 0) {
      setTimeout(() => {
        const elementBounds = elementIds
          .map(id => edgelessService.getElementById(id)?.elementBound)
          .filter((bound): bound is Bound => bound !== undefined);

        const overallBound = getCommonBound(elementBounds);
        if (overallBound) {
          edgelessService.setViewportWithBoundAtBottom(overallBound, [10, 10, 10, 10]);
        }
      }, 0);
    }
  },

  createQuiz: async (host: EditorHost, ctx: CtxRecord) => {
    const aiPanel = getAIPanel(host);
    if (!aiPanel) return;
    const edgelessService = getEdgelessService(host);
    const edgelessCopilot = getEdgelessCopilotWidget(host);

    const renderer = aiPanel.shadowRoot?.querySelector(
      'ai-quiz-renderer'
    ) as AIQuizRenderer;
    if (!renderer) return;

    await renderer.templateReady;

    const data = ctx.get();
    const quiz = data['data'];
    if (!quiz) throw new Error('No quiz data provided');

    const questions = (quiz as Record<string, unknown>)['questions'];
    if (!Array.isArray(questions) || questions.length === 0) {
      throw new Error('No valid questions in quiz data');
    }

    edgelessCopilot.hideCopilotPanel();
    aiPanel.hide();

    const noteBlockId = edgelessService.addFullViewportBlock();
    edgelessService.addBlock(
      'affine:embed-pique-quiz',
      {
        quiz: JSON.stringify(quiz),
        style: 'html',
        html: '',
      },
      noteBlockId
    );

    setTimeout(() => {
      edgelessService.selection.set({
        elements: [noteBlockId],
        editing: false,
      });
    });
  },

  makeItReal: (host, ctx) => {
    const aiPanel = getAIPanel(host);
    if (!aiPanel) return;
    let html = aiPanel.answer;
    if (!html) return;
    html = preprocessHtml(html);

    const surface = host.doc.getBlocksByFlavour('affine:surface')[0]
      ?.model as SurfaceBlockModel | undefined;
    if (!surface) return;

    const data = ctx.get();
    const bounds = determineInsertionBounds(
      host,
      (data['width'] as number | undefined) ?? 800,
      (data['height'] as number | undefined) ?? 600
    );
    const edgelessCopilot = getEdgelessCopilotWidget(host);

    edgelessCopilot.hideCopilotPanel();
    aiPanel.hide();

    const edgelessRoot = getEdgelessRootFromEditor(host); // Retrieve the edgeless root service.

    host.doc.transact(() => {
      edgelessRoot.doc.addBlock(
        'affine:embed-html',
        {
          html, // Assign the preprocessed HTML content.
          design: 'ai:makeItReal', // Tag to identify the design or purpose.
          xywh: bounds.serialize(),
        },
        surface.id
      );
    });
  },

  createSlides: (host, _ctx) => {
    // Note: This action is currently not being called.The actual slide creation and grouping happens in PPTBuilder (packages/frontend/core/src/blocksuite/presets/ai/slides/index.ts)
    //
    //
    // TODO: Move the final zoom from packages/frontend/core/src/blocksuite/presets/ai/messages/slides-processor.ts to here

    const edgelessService = getEdgelessService(host);

    edgelessService.selection.set({
      elements: [],
      editing: false,
      inoperable: false,
    });
  },

  createImage: imageHandler,
  processImage: imageHandler,
  filterImage: imageHandler,
};

export function actionToGenerating<T extends keyof BlockSuitePresets.AIActions>(
  id: T,
  generatingIcon: TemplateResult<1>
) {
  return {
    generatingIcon,
    stages: generatingStages[id] ?? [],
  };
}
